import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, IdCard, Inbox, CalendarDays, ScanLine, ClipboardList, FileSpreadsheet, UserCog, Settings, LogOut, Menu, KeyRound, BadgeCheck, Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo, useSettings } from "@/components/common";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { PasswordDialog } from "@/components/PasswordDialog";

const MENU = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "sector_manager"] },
  { to: "/atleti", label: "Atleti", icon: Users, roles: ["admin", "sector_manager"] },
  { to: "/licenze", label: "Licenze", icon: IdCard, roles: ["admin", "sector_manager"] },
  { to: "/richieste", label: "Richieste", icon: Inbox, roles: ["admin", "sector_manager"] },
  { to: "/manifestazioni", label: "Manifestazioni", icon: CalendarDays, roles: ["admin", "sector_manager", "organizer"] },
  { to: "/scanner", label: "Scanner QR", icon: ScanLine, roles: ["admin", "organizer"] },
  { to: "/partecipazioni", label: "Partecipazioni", icon: ClipboardList, roles: ["admin", "sector_manager", "organizer"] },
  { to: "/report", label: "Report", icon: FileSpreadsheet, roles: ["admin", "sector_manager", "organizer"] },
  { to: "/utenti", label: "Utenti", icon: UserCog, roles: ["admin"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
  { to: "/mia-licenza", label: "La mia area", icon: BadgeCheck, roles: ["athlete"] },
  { to: "/mie-gare", label: "Le mie gare", icon: Flag, roles: ["athlete"] },
];

function NavItems({ role, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {MENU.filter((m) => m.roles.includes(role)).map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${to.slice(1)}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${isActive ? "bg-white/10 text-white" : "text-blue-100/70 hover:bg-white/5 hover:text-white"}`}>
          <Icon className="h-[18px] w-[18px] shrink-0" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ user, settings, onNavigate, onLogout, onPassword }) {
  return (
    <div className="flex h-full flex-col bg-[#0F2042] px-4 py-6 text-white">
      <div className="mb-8 flex items-center gap-3 px-2">
        <Logo settings={settings} dark className="h-10 w-10" />
        <div className="leading-tight">
          <div className="font-display text-base font-bold tracking-tight" data-testid="app-brand-name">Passrace</div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400">MSP Italia · Motori</div>
        </div>
      </div>
      <NavItems role={user.role} onNavigate={onNavigate} />
      <div className="mt-auto border-t border-white/10 pt-4">
        <div className="px-2 text-sm font-semibold" data-testid="current-user-name">{user.name}</div>
        <div className="px-2 text-xs text-blue-100/60" data-testid="current-user-role">{user.role_label}{user.sector_name ? ` · ${user.sector_name}` : ""}</div>
        <div className="mt-3 flex gap-2">
          <button onClick={onPassword} data-testid="change-password-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-xs font-medium text-blue-100 transition-colors hover:bg-white/10">
            <KeyRound className="h-4 w-4" /> Password
          </button>
          <button onClick={onLogout} data-testid="logout-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-xs font-medium text-blue-100 transition-colors hover:bg-red-500/20">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };
  const body = (close) => <SidebarBody user={user} settings={settings} onNavigate={close} onLogout={doLogout} onPassword={() => { close?.(); setPwd(true); }} />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block no-print">{body()}</aside>
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:hidden no-print">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-btn" className="rounded-lg p-2 text-slate-700 hover:bg-slate-100" aria-label="Apri menu"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-none p-0">{body(() => setOpen(false))}</SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <Logo settings={settings} className="h-8 w-8" />
          <span className="font-display text-sm font-bold text-[#0F2042]">Passrace · MSP Italia</span>
        </div>
        <div className="w-9" />
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10"><Outlet /></div>
      </main>
      <PasswordDialog open={pwd} onOpenChange={setPwd} />
    </div>
  );
}
